import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { 
  CheckCircle2,
  Calendar,
  MessageCircle,
  Target,
  TrendingUp,
  Shield,
  BarChart3,
  Zap,
  Phone,
  AlertCircle,
  X
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const PricingStrategyConsultant = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    businessType: '',
    challenge: ''
  });
  const [showForm, setShowForm] = useState(false);

  const calendlyLink = "https://calendly.com/theprofitprophet/pricing-diagnostic";
  const whatsappLink = "https://wa.me/27659994443?text=Hi%20I%20need%20a%20pricing%20diagnostic";

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // GTM event tracking
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'lead_form_submit',
        formData: formData
      });
    }
    // Handle form submission (e.g., send to backend)
    alert('Thank you! We will contact you within 24 hours.');
    setFormData({ fullName: '', phone: '', businessType: '', challenge: '' });
  };

  const trackEvent = (eventName) => {
    if (window.dataLayer) {
      window.dataLayer.push({ event: eventName });
    }
  };

  const services = [
    {
      icon: <Target className="w-12 h-12 text-gold" />,
      title: "Pricing Intelligence Engine™",
      description: "Structured quoting logic, margin floors, escalation rules"
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-gold" />,
      title: "Revenue Foundations",
      description: "Service packaging, price laddering, value-based architecture"
    },
    {
      icon: <Shield className="w-12 h-12 text-gold" />,
      title: "Margin Control System",
      description: "Discount governance, approval workflows, realised margin tracking"
    },
    {
      icon: <BarChart3 className="w-12 h-12 text-gold" />,
      title: "Profitability Analytics",
      description: "Client-level and service-line profitability dashboards"
    },
    {
      icon: <Zap className="w-12 h-12 text-gold" />,
      title: "Digital Growth Integrity",
      description: "Demand generation without destroying pricing discipline"
    }
  ];

  const faqs = [
    {
      question: "Do you work with small firms?",
      answer: "Yes. We work with professional firms from 10–200 staff. If you have negotiated pricing, recurring clients, or project-based billing, we can help improve profitability regardless of size."
    },
    {
      question: "How long does implementation take?",
      answer: "A Diagnostic Sprint takes 4–6 weeks. Full implementation ranges from 8–16 weeks depending on complexity. You'll see quick wins within the first month."
    },
    {
      question: "Do you replace our accountant?",
      answer: "No. We complement your existing financial team. We focus specifically on pricing architecture, margin governance, and profitability systems — not tax, compliance, or bookkeeping."
    },
    {
      question: "Will this slow down our sales process?",
      answer: "No. The Pricing Intelligence Engine is designed to speed up quoting while enforcing margin discipline. You'll close faster with better profitability."
    },
    {
      question: "Is our data confidential?",
      answer: "Absolutely. All client engagements are covered by strict confidentiality agreements. We never share your pricing data, client information, or strategic details."
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "name": "The Profit Prophet",
        "url": "https://www.theprofitprophet.co.za",
        "logo": "https://www.theprofitprophet.co.za/logo.png",
        "founder": {
          "@type": "Person",
          "name": "Rohan Anson Basson"
        },
        "areaServed": "ZA",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+27-65-999-4443",
          "contactType": "sales"
        }
      },
      {
        "@type": "Service",
        "serviceType": "Pricing Strategy Consulting",
        "provider": {
          "@type": "Organization",
          "name": "The Profit Prophet"
        },
        "areaServed": "ZA",
        "description": "Premium pricing and profitability consulting for South African professional firms. Recover 8–14% margin leakage through engineered pricing systems."
      },
      {
        "@type": "LocalBusiness",
        "name": "The Profit Prophet",
        "image": "https://www.theprofitprophet.co.za/logo.png",
        "@id": "https://www.theprofitprophet.co.za",
        "url": "https://www.theprofitprophet.co.za",
        "telephone": "+27659994443",
        "priceRange": "R75000-R150000+",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "ZA"
        },
        "areaServed": "ZA"
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Pricing Strategy Consultant South Africa | The Profit Prophet</title>
        <meta name="description" content="Premium pricing & profitability consulting for South African professional firms. Recover 8–14% margin leakage. Book your diagnostic today." />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-dark">
        {/* Sticky CTAs - Mobile Only */}
        <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-dark border-t border-gold/20 p-4 flex gap-3">
          <Button
            className="flex-1 bg-gold hover:bg-gold-light text-dark font-bold"
            onClick={() => {
              trackEvent('pricing_diagnostic_click');
              window.open(calendlyLink, '_blank');
            }}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Book Diagnostic
          </Button>
          <Button
            variant="outline"
            className="flex-1 border-blue text-blue hover:bg-blue/10"
            onClick={() => {
              trackEvent('whatsapp_click');
              window.open(whatsappLink, '_blank');
            }}
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            WhatsApp
          </Button>
        </div>

        {/* Header */}
        <header className="py-6 px-6 border-b border-gold/20">
          <div className="container mx-auto max-w-6xl">
            <a href="/" className="inline-block">
              <h3 className="text-2xl font-bold text-gold">The Profit Prophet</h3>
            </a>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 px-6 bg-gradient-to-b from-dark to-dark-light">
          <div className="container mx-auto max-w-6xl">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Stop leaking <span className="text-gold">8–14% margins</span> every month on bad pricing.
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-300 mb-4">
                Engineered profitability systems installed in weeks — not guesswork.
              </p>
              
              <p className="text-sm text-gray-400 mb-10 max-w-2xl mx-auto">
                Built specifically for South African professional firms where pricing is negotiated, 
                bespoke, and margin leakage hides in plain sight.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button
                  size="lg"
                  className="bg-gold hover:bg-gold-light text-dark font-bold text-lg px-10 py-6"
                  onClick={() => {
                    trackEvent('pricing_diagnostic_click');
                    window.open(calendlyLink, '_blank');
                  }}
                >
                  <Calendar className="mr-3 w-5 h-5" />
                  Book Free Pricing Diagnostic
                </Button>
                
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-blue text-blue hover:bg-blue/10 font-semibold text-lg px-10 py-6"
                  onClick={() => {
                    trackEvent('whatsapp_click');
                    window.open(whatsappLink, '_blank');
                  }}
                >
                  <MessageCircle className="mr-3 w-5 h-5" />
                  Chat on WhatsApp
                </Button>
              </div>
              
              <div className="flex items-center justify-center gap-6 text-sm text-gray-400">
                <span className="flex items-center">
                  <CheckCircle2 className="w-4 h-4 mr-2 text-gold" />
                  Confidential
                </span>
                <span className="flex items-center">
                  <CheckCircle2 className="w-4 h-4 mr-2 text-gold" />
                  Senior-led
                </span>
                <span className="flex items-center">
                  <CheckCircle2 className="w-4 h-4 mr-2 text-gold" />
                  SA Focused
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-20 px-6 bg-dark">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center">
              Why professional firms quietly lose profit
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
              {[
                "Discounts approved emotionally to close deals",
                "No enforced minimum margin guardrails",
                "Pricing varies by partner or manager",
                "Scope creep destroys realised profit",
                "No visibility into service-line profitability"
              ].map((problem, index) => (
                <div key={index} className="flex items-start bg-white/5 p-6 rounded-lg border border-gold/20">
                  <AlertCircle className="w-6 h-6 text-gold mr-4 flex-shrink-0 mt-1" />
                  <p className="text-gray-300 text-lg">{problem}</p>
                </div>
              ))}
            </div>
            
            <p className="text-xl text-center text-gray-300 max-w-3xl mx-auto">
              Most firms think they have a <span className="text-white font-semibold">revenue problem</span>.<br />
              They actually have a <span className="text-gold font-semibold">pricing architecture problem</span>.
            </p>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-20 px-6 bg-gradient-to-b from-dark-light to-dark">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 text-center">
              The Pricing Intelligence Engine™
            </h2>
            
            <p className="text-xl text-gray-300 mb-16 max-w-3xl mx-auto text-center leading-relaxed">
              The Profit Prophet installs a structured pricing and profitability system that standardizes 
              quoting, enforces margin floors, and improves realised margin — without slowing down your sales process.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {[
                { num: "1", title: "Diagnose margin leakage" },
                { num: "2", title: "Install pricing architecture" },
                { num: "3", title: "Enforce governance + tracking" }
              ].map((step) => (
                <div key={step.num} className="text-center">
                  <div className="w-16 h-16 bg-gold text-dark font-bold text-2xl rounded-full flex items-center justify-center mx-auto mb-4">
                    {step.num}
                  </div>
                  <p className="text-white font-semibold text-lg">{step.title}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 px-6 bg-dark">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <Card key={index} className="bg-white/5 border-gold/30 hover:border-gold transition-all">
                  <CardContent className="p-8 text-center">
                    <div className="mb-6 flex justify-center">{service.icon}</div>
                    <h3 className="text-xl font-bold text-white mb-4">{service.title}</h3>
                    <p className="text-gray-400">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Authority Section */}
        <section className="py-20 px-6 bg-gradient-to-b from-dark-light to-dark">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center">
              Commercial results, not theory.
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-16 max-w-4xl mx-auto">
              <div className="text-center border-l-2 border-gold pl-6">
                <div className="text-4xl font-bold text-gold mb-2">R20M+</div>
                <div className="text-gray-400 text-sm">Revenue uplift across engagements</div>
              </div>
              <div className="text-center border-l-2 border-gold pl-6">
                <div className="text-4xl font-bold text-gold mb-2">8-14%</div>
                <div className="text-gray-400 text-sm">Margin recovery typical</div>
              </div>
              <div className="text-center border-l-2 border-gold pl-6">
                <div className="text-4xl font-bold text-gold mb-2">Master's AI</div>
                <div className="text-gray-400 text-sm">Economics + AI background</div>
              </div>
            </div>
            
            <div className="bg-white/5 p-8 rounded-lg border border-gold/20 max-w-3xl mx-auto mb-12">
              <h3 className="text-2xl font-bold text-white mb-4">Rohan Anson Basson</h3>
              <p className="text-gold mb-4">Founder, The Profit Prophet</p>
              <p className="text-gray-300 leading-relaxed">
                Economics background • Master's in Artificial Intelligence • Pricing & profitability specialist
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                "Engineering consultancy improved realised margin via structured pricing enforcement",
                "Medical practice group redesigned service packaging and eliminated underpriced consults",
                "Professional services firm reduced uncontrolled discounting by installing approval guardrails"
              ].map((example, index) => (
                <div key={index} className="bg-white/5 p-6 rounded-lg border border-gold/20">
                  <p className="text-gray-300 text-sm italic">{example}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Target Audience */}
        <section className="py-20 px-6 bg-dark">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center">
              Is this for you?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-green-900/20 border border-green-500/30 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-green-400 mb-6">Good fit</h3>
                <ul className="space-y-3 text-gray-300">
                  {[
                    "10–200 staff professional firms",
                    "Repeat engagements or project-based pricing",
                    "Negotiated or custom quotes",
                    "Fee pressure or discounting challenges",
                    "Growing revenue but stagnant profit"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-red-900/20 border border-red-500/30 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-red-400 mb-6">Not a fit</h3>
                <ul className="space-y-3 text-gray-300">
                  {[
                    "Early-stage startups",
                    "Commodity-only price takers",
                    "Businesses looking for marketing-only solutions"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <X className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 px-6 bg-gradient-to-b from-dark-light to-dark">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center">
              Clear investment structure
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <Card className="bg-white/5 border-2 border-gold/50">
                <CardContent className="p-10">
                  <h3 className="text-2xl font-bold text-white mb-4">Diagnostic Sprint</h3>
                  <div className="text-4xl font-bold text-gold mb-6">From R75,000</div>
                  <ul className="space-y-3 text-gray-300 mb-6">
                    {[
                      "Margin leakage audit",
                      "Pricing architecture review",
                      "Implementation blueprint",
                      "Executive presentation"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-gold mr-3 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 border-2 border-blue/50">
                <CardContent className="p-10">
                  <h3 className="text-2xl font-bold text-white mb-4">Full Implementation</h3>
                  <div className="text-4xl font-bold text-blue mb-6">From R150,000+</div>
                  <ul className="space-y-3 text-gray-300 mb-6">
                    {[
                      "Engine build",
                      "Governance installation",
                      "Dashboards",
                      "Rollout support"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-blue mr-3 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
            
            <p className="text-center text-gray-400 mt-8 text-sm italic">
              Final scope depends on service complexity and implementation depth.
            </p>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 px-6 bg-dark">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center">
              What happens next?
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {[
                { num: "1", title: "Book a Free 15-Min Diagnostic" },
                { num: "2", title: "Identify pricing leakage + quick wins" },
                { num: "3", title: "Receive a structured Sprint proposal" }
              ].map((step) => (
                <div key={step.num} className="text-center bg-white/5 p-8 rounded-lg border border-gold/20">
                  <div className="text-5xl font-bold text-gold mb-4">{step.num}</div>
                  <p className="text-white font-semibold text-lg">{step.title}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Lead Capture Form */}
        <section className="py-20 px-6 bg-gradient-to-b from-dark-light to-dark">
          <div className="container mx-auto max-w-2xl">
            <div className="bg-white/5 p-10 rounded-lg border-2 border-gold">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">
                Request Strategy Call
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Input
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="bg-white/10 border-gold/30 text-white placeholder:text-gray-400"
                  />
                </div>
                
                <div>
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="bg-white/10 border-gold/30 text-white placeholder:text-gray-400"
                  />
                </div>
                
                <div>
                  <Input
                    type="text"
                    name="businessType"
                    placeholder="Business Type (e.g., Engineering Firm, Medical Practice)"
                    value={formData.businessType}
                    onChange={handleInputChange}
                    required
                    className="bg-white/10 border-gold/30 text-white placeholder:text-gray-400"
                  />
                </div>
                
                <div>
                  <Textarea
                    name="challenge"
                    placeholder="Biggest Pricing Challenge"
                    value={formData.challenge}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="bg-white/10 border-gold/30 text-white placeholder:text-gray-400"
                  />
                </div>
                
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-gold hover:bg-gold-light text-dark font-bold text-lg py-6"
                >
                  Request Strategy Call
                </Button>
              </form>
              
              <Separator className="my-8 bg-gold/20" />
              
              <div className="text-center">
                <p className="text-gray-400 mb-4">Or message directly via WhatsApp</p>
                <Button
                  variant="outline"
                  className="border-2 border-blue text-blue hover:bg-blue/10"
                  onClick={() => {
                    trackEvent('whatsapp_click');
                    window.open(whatsappLink, '_blank');
                  }}
                >
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Chat on WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-6 bg-dark">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white/5 p-8 rounded-lg border border-gold/20">
                  <h3 className="text-xl font-bold text-gold mb-4">{faq.question}</h3>
                  <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final Urgency CTA */}
        <section className="py-20 px-6 bg-gradient-to-b from-dark-light to-dark">
          <div className="container mx-auto max-w-3xl text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Limited March 2026 sessions — book now before spots fill.
            </h2>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-gold hover:bg-gold-light text-dark font-bold text-lg px-12 py-7"
                onClick={() => {
                  trackEvent('pricing_diagnostic_click');
                  window.open(calendlyLink, '_blank');
                }}
              >
                <Calendar className="mr-3 w-6 h-6" />
                Book Free Pricing Diagnostic
              </Button>
              
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-blue text-blue hover:bg-blue/10 font-semibold text-lg px-12 py-7"
                onClick={() => {
                  trackEvent('phone_click');
                  window.location.href = 'tel:+27659994443';
                }}
              >
                <Phone className="mr-3 w-6 h-6" />
                Call Now
              </Button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-dark-light py-8 px-6 border-t border-gold/20">
          <div className="container mx-auto max-w-6xl text-center">
            <p className="text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} The Profit Prophet. Pricing Strategy Consultant South Africa.
            </p>
            <div className="mt-3 flex justify-center items-center gap-4 text-sm">
              <a href="/privacy-policy" className="text-gray-400 hover:text-gold transition-colors">
                Privacy Policy
              </a>
            </div>
            
            <Separator className="my-6 bg-gold/10 max-w-2xl mx-auto" />
            
            <div className="mt-6 max-w-3xl mx-auto">
              <p className="text-gray-500 text-xs font-semibold mb-3 uppercase tracking-wider">Legal / Business Information</p>
              <p className="text-gray-400 text-xs leading-relaxed mb-2">
                The Profit Prophet is a pricing and profitability advisory platform founded and operated by Rohan Anson Basson, 
                based in Johannesburg, South Africa.
              </p>
              <p className="text-gray-400 text-xs leading-relaxed">
                The platform provides AI-powered pricing assistance for contractors, service businesses, and SMEs 
                looking to structure profitable quotes.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PricingStrategyConsultant;
